import {
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  Music2,
  Twitter,
  Youtube,
  type LucideIcon,
} from 'lucide-react';
import { formatSocialUrl } from '@/utils/formatSocialUrl';
import type { Socials } from '@/types';

interface SocialLinksProps {
  socials?: Socials;
  website?: string;
  /** Called with the resolved URL when the website link is opened (analytics). */
  onWebsiteClick?: (url: string) => void;
}

interface SocialLinkItem {
  key: string;
  label: string;
  icon: LucideIcon;
  url: string;
}

export function SocialLinks({ socials, website, onWebsiteClick }: SocialLinksProps) {
  const items: SocialLinkItem[] = [];

  if (website) {
    const url = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    items.push({ key: 'website', label: 'Website', icon: Globe, url });
  }

  const platforms = [
    { key: 'facebook', platform: 'facebook', label: 'Facebook', icon: Facebook },
    { key: 'instagram', platform: 'instagram', label: 'Instagram', icon: Instagram },
    { key: 'twitter', platform: 'twitter', label: 'Twitter', icon: Twitter },
    { key: 'youtube', platform: 'youtube', label: 'YouTube', icon: Youtube },
    { key: 'linkedIn', platform: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { key: 'tiktok', platform: 'tiktok', label: 'TikTok', icon: Music2 },
  ] as const;

  for (const { key, platform, label, icon } of platforms) {
    const value = socials?.[key];
    if (!value) continue;
    const url = formatSocialUrl(platform, value);
    if (url) items.push({ key, label, icon, url });
  }

  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {items.map(({ key, label, icon: Icon, url }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={key === 'website' && onWebsiteClick ? () => onWebsiteClick(url) : undefined}
          aria-label={label}
          title={label}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary transition-all duration-200 hover:bg-primary hover:text-white"
        >
          <Icon size={18} />
        </a>
      ))}
    </div>
  );
}

export default SocialLinks;
